import { useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { Text } from 'foundations-components/components/Text';
import { Spacer } from 'foundations-components/components/Spacer';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { DateIcon, ShareIcon } from 'foundations-assets';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BREAKPOINTS } from 'foundations-components/transitional/components/Grid';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import I18n from 'I18n';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import ProfilePageHeroBackground from './ProfilePageHeroBackground';
import { Role } from '../../components/Role';
import { SocialLinks } from '../../components/SocialLinks';
import { useMediaQuery } from '../../../../utils/Styling';
import { ContentWrapper } from '../../components/ContentWrapper';
import { MakeProfilePublicModal } from './MakeProfilePublicModal';
import { generatePublicProfileUrl } from '../../utils/profileUrls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ImageWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__ImageWrapper",
  componentId: "k8o8se-0"
})(["display:flex;align-items:center;justify-content:center;grid-area:avatar;@media (max-width:", "px){justify-content:flex-start;padding-block-end:", ";}"], BREAKPOINTS.sm, ({
  theme
}) => theme.space['300']);
const NameWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__NameWrapper",
  componentId: "k8o8se-1"
})(["grid-area:name;"]);
const SocialWrapper = styled.div.withConfig({
  displayName: "ProfilePageHeader__SocialWrapper",
  componentId: "k8o8se-2"
})(["grid-area:social;"]);
const GridContainer = styled.div.withConfig({
  displayName: "ProfilePageHeader__GridContainer",
  componentId: "k8o8se-3"
})(["display:grid;width:100%;grid-template-columns:auto auto 1fr;grid-template-areas:'avatar name social';gap:", ";@media (max-width:", "px){grid-template-columns:1fr auto;grid-template-areas:'avatar' 'name' 'social';gap:", ";}"], ({
  theme
}) => theme.space['400'], BREAKPOINTS.sm, ({
  theme
}) => theme.space['0']);
const ActionButtons = ({
  userProfile,
  editPath,
  onOpenModal,
  trackInteraction
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const publicUrl = generatePublicProfileUrl(userProfile.userId);
  return /*#__PURE__*/_jsxs(ButtonWrapper, {
    alignItems: "center",
    children: [userProfile.public ? /*#__PURE__*/_jsxs(CopyButton, {
      "data-test-id": "copy-button",
      onClick: () => {
        trackInteraction('share-profile');
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('profilePage.share.messages.success')
        });
      },
      size: isMobile ? 'medium' : 'small',
      use: "tertiary-light",
      value: publicUrl,
      children: [/*#__PURE__*/_jsx(ShareIcon, {
        size: "300"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.share.button.shareProfile"
      })]
    }) : /*#__PURE__*/_jsxs(Button, {
      "data-test-id": "open-mode-button",
      onClick: () => {
        onOpenModal();
      },
      size: isMobile ? 'medium' : 'small',
      use: "tertiary-light",
      children: [/*#__PURE__*/_jsx(ShareIcon, {
        size: "300"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.share.button.shareProfile"
      })]
    }), /*#__PURE__*/_jsx(UIRouterButton, {
      size: isMobile ? 'medium' : 'small',
      to: editPath,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.header.editProfileButton"
      })
    })]
  });
};
export const ProfilePageHeader = ({
  userProfile,
  hasEditAccess,
  editPath,
  trackInteraction
}) => {
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const [makePublicModalOpen, setMakePublicModalOpen] = useState(false);
  return /*#__PURE__*/_jsxs(ProfilePageHeroBackground, {
    children: [/*#__PURE__*/_jsx(ContentWrapper, {
      children: /*#__PURE__*/_jsx(View, {
        paddingBlock: isMobile ? '600' : '1100',
        paddingInline: isTablet ? '500' : '0',
        children: /*#__PURE__*/_jsxs(GridContainer, {
          children: [/*#__PURE__*/_jsx(ImageWrapper, {
            children: /*#__PURE__*/_jsx(Image, {
              inlineSize: isMobile ? '60px' : '80px',
              marginInlineEnd: "400",
              shape: "circle",
              src: userProfile.avatarUrl
            })
          }), /*#__PURE__*/_jsx(NameWrapper, {
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              children: [/*#__PURE__*/_jsxs(Flex, {
                alignItems: isMobile ? 'start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h1",
                  color: "text-core-on-fill-default",
                  typography: isMobile ? 'heading-400' : 'heading-600',
                  children: userProfile.displayName
                }), userProfile.pronouns && /*#__PURE__*/_jsx(Text, {
                  color: "text-core-on-fill-default",
                  marginInlineStart: isMobile ? '0' : '400',
                  paddingBlockEnd: "200",
                  typography: isMobile ? 'detail-200' : 'heading-25',
                  children: userProfile.pronouns
                })]
              }), /*#__PURE__*/_jsx(Role, {
                companyName: userProfile.companyName,
                jobTitle: userProfile.jobTitle
              }), userProfile.createdAt ? /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                paddingBlockStart: "200",
                children: [/*#__PURE__*/_jsx(DateIcon, {
                  color: "text-core-on-fill-default"
                }), /*#__PURE__*/_jsx(Spacer, {
                  display: "inline-block",
                  space: "200"
                }), /*#__PURE__*/_jsx(Text, {
                  color: "text-core-on-fill-default",
                  typography: isMobile ? 'detail-200' : 'heading-25',
                  children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                    elements: {
                      Date: FormattedDateTime
                    },
                    message: "profilePage.profileHeader.joinedDate_jsx",
                    options: {
                      value: userProfile.createdAt,
                      format: 'short-m-y'
                    }
                  })
                })]
              }) : null]
            })
          }), /*#__PURE__*/_jsx(SocialWrapper, {
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: isMobile ? undefined : 'end',
              flexDirection: "column",
              flexGrow: 1,
              children: [/*#__PURE__*/_jsx(SocialLinks, {
                userProfile: userProfile
              }), hasEditAccess && /*#__PURE__*/_jsx(ActionButtons, {
                editPath: editPath,
                onOpenModal: () => setMakePublicModalOpen(true),
                trackInteraction: trackInteraction,
                userProfile: userProfile
              })]
            })
          })]
        })
      })
    }), makePublicModalOpen && /*#__PURE__*/_jsx(MakeProfilePublicModal, {
      onClose: () => setMakePublicModalOpen(false),
      trackInteraction: trackInteraction,
      userProfile: userProfile
    })]
  });
};